import type { StripeProducts } from '@racemap/sdk/schema/billing/StripeProducts';
import React, { type FC } from 'react';
import type { useGetProductPrice } from 'src/lib/customHooks/useGetProductPrice';
import { CompactNumber } from 'src/misc/formatNumber';
import { formatPrice } from 'src/misc/formatPrice';
import { getLabelForBillingItemType } from 'src/misc/getLabelForBillingItemType';

interface Props {
  products: Array<[StripeProducts, number]>;
  getPrice: ReturnType<typeof useGetProductPrice>;
  creditsLabel: string;
  // Credit units for a product, or undefined when it has none.
  getCredits: (product: StripeProducts) => number | undefined;
  hidePrice?: (product: StripeProducts) => boolean;
}

export const ProductsTable: FC<Props> = ({
  products,
  getPrice,
  creditsLabel,
  getCredits,
  hidePrice = () => false,
}) => (
  <table>
    <thead>
      <tr>
        <th className="labelColumn">Product</th>
        <th className="unitColumn">Units</th>
        <th>Price per Unit</th>
        <th>Total Price</th>
      </tr>
    </thead>
    <tbody>
      {products.map(([productKey, amount]) => {
        const credits = getCredits(productKey);
        return (
          <React.Fragment key={productKey}>
            <tr>
              <td className="labelColumn">{getLabelForBillingItemType(productKey)}</td>
              <td className="unitColumn">
                <CompactNumber value={amount} />
              </td>
              <td>{!hidePrice(productKey) && formatPrice(getPrice?.(productKey, 1))}</td>
              <td>{!hidePrice(productKey) && formatPrice(getPrice?.(productKey, amount))}</td>
            </tr>
            {credits != null && (
              <tr>
                <td className="labelColumn">
                  {creditsLabel} {getLabelForBillingItemType(productKey)}
                </td>
                <td className="unitColumn">
                  <CompactNumber value={credits} />
                </td>
                <td />
                <td>- {formatPrice(getPrice?.(productKey, credits))}</td>
              </tr>
            )}
          </React.Fragment>
        );
      })}
    </tbody>
  </table>
);
